/**
 * LanguageSwitcher — dropdown in the top nav. Persists the choice in
 * localStorage via i18next's language detector.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Check } from 'lucide-react';
import { SUPPORTED_LANGUAGES } from '../../i18n';

const LanguageSwitcher = ({ compact = true }) => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  // Normalise: browser detector often returns "en-US" / "es-ES" — collapse
  // to the base 2-letter code so our SUPPORTED_LANGUAGES lookup succeeds.
  const rawLang = (i18n.language || 'en').split('-')[0].toLowerCase();
  const current = SUPPORTED_LANGUAGES.find((l) => l.code === rawLang) || SUPPORTED_LANGUAGES[0];

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    // Use bubble phase so our toggle click has fully processed first.
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const pick = (code) => {
    i18n.changeLanguage(code);
    try { localStorage.setItem('fda_lang', code); } catch { /* ignore */ }
    setOpen(false);
    // Hard reload so every `useTranslation()`, `useAutoTranslate()` and cached
    // API response picks up the new language everywhere on the site — the
    // previous "silent" changeLanguage only updated components that were
    // actively subscribed via useTranslation, so most of the UI stayed in
    // the old language.
    setTimeout(() => { try { window.location.reload(); } catch { /* ignore */ } }, 50);
  };

  return (
    <div ref={wrapRef} className="relative" data-testid="language-switcher">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        className="inline-flex items-center gap-1.5 rounded-full border border-border px-2.5 py-1 text-xs font-bold hover:bg-muted transition-colors"
        aria-label="Change language"
        aria-expanded={open}
        data-testid="language-switcher-toggle"
      >
        <Globe className="w-3.5 h-3.5" />
        {compact ? current.code.toUpperCase() : current.label}
      </button>
      {open && (
        <div
          className="absolute right-0 mt-1 w-40 rounded-lg border border-border bg-popover shadow-xl z-[9999]"
          data-testid="language-switcher-menu"
        >
          {SUPPORTED_LANGUAGES.map((l) => (
            <button
              key={l.code}
              type="button"
              onClick={() => pick(l.code)}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center justify-between ${l.code === current.code ? 'text-primary font-bold' : ''}`}
              data-testid={`language-switcher-option-${l.code}`}
            >
              {l.label}
              {l.code === current.code && <Check className="w-3.5 h-3.5" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
